import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useUpdateInternalProfile } from "@/hooks/useInternalProfile";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from "@/hooks/useNotificationPreferences";
import { useAuth } from "@/lib/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { Bell, Lock, Shield, User } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const profileSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email"),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

export default function Settings() {
  const { user } = useAuth();
  const updateProfile = useUpdateInternalProfile();
  const prefsQuery = useNotificationPreferences();
  const updatePrefs = useUpdateNotificationPreferences();
  const prefs = prefsQuery.data;

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: "", email: "" },
  });

  useEffect(() => {
    if (user) {
      reset({ name: user.name ?? "", email: user.email });
    }
  }, [user, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await updateProfile.mutateAsync({
        name: values.name,
        email: values.email,
      });
      reset(values);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Update failed";
      if (message.toLowerCase().includes("already in use")) {
        setError("email", { type: "manual", message: "Email already in use" });
      }
    }
  });

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight">
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your account and preferences
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Profile</CardTitle>
          </div>
          <CardDescription>Your admin account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Display Name</Label>
                <Input
                  id="name"
                  data-testid="input-settings-name"
                  {...register("name")}
                />
                {errors.name ? (
                  <p className="text-sm text-destructive">
                    {errors.name.message}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-settings-email"
                  {...register("email")}
                />
                {errors.email ? (
                  <p className="text-sm text-destructive">
                    {errors.email.message}
                  </p>
                ) : null}
              </div>
            </div>
            <Button
              type="submit"
              disabled={!isDirty || isSubmitting}
              data-testid="button-save-profile"
            >
              {isSubmitting ? "Saving…" : "Save Changes"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Notifications</CardTitle>
          </div>
          <CardDescription>
            Configure how you receive alerts and updates
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {prefsQuery.isLoading || !prefs ? (
            <>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>System Alerts</Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified about system health issues
                  </p>
                </div>
                <Switch
                  checked={prefs.systemAlerts}
                  onCheckedChange={(value) =>
                    updatePrefs.mutate({ systemAlerts: value })
                  }
                  data-testid="switch-system-alerts"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Content Reviews</Label>
                  <p className="text-sm text-muted-foreground">
                    Notifications for pending content reviews
                  </p>
                </div>
                <Switch
                  checked={prefs.contentReviews}
                  onCheckedChange={(value) =>
                    updatePrefs.mutate({ contentReviews: value })
                  }
                  data-testid="switch-content-reviews"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Weekly Reports</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive weekly engagement summaries
                  </p>
                </div>
                <Switch
                  checked={prefs.weeklyReports}
                  onCheckedChange={(value) =>
                    updatePrefs.mutate({ weeklyReports: value })
                  }
                  data-testid="switch-weekly-reports"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Security</CardTitle>
          </div>
          <CardDescription>
            Manage your account security settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Label>Two-Factor Authentication</Label>
                <Badge variant="secondary">Coming Soon</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Add an extra layer of security to your account
              </p>
            </div>
            <Button variant="outline" disabled data-testid="button-setup-2fa">
              Set Up
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Privacy Guardrails</CardTitle>
          </div>
          <CardDescription>
            Privacy protections are always active and cannot be disabled
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-md bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300">
              <Shield className="h-4 w-4" />
              <span className="text-sm font-medium">
                User emotional data is protected and never displayed
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-md bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300">
              <Shield className="h-4 w-4" />
              <span className="text-sm font-medium">
                Journal entries are encrypted and inaccessible
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-md bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300">
              <Shield className="h-4 w-4" />
              <span className="text-sm font-medium">
                Menstrual cycle data is never shown individually
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-md bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300">
              <Shield className="h-4 w-4" />
              <span className="text-sm font-medium">
                Admin actions are fully audited and logged
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
